/**
 * 시딩 캠페인 상세 페이지 도메인 타입.
 *
 * components-spec.md §6 의 DTO shape 을 코드로 옮긴 것.
 * 17 상태(모집형 9 + 제안형 7 + 공통 1) 가 단일 `SeedingCampaignDetail` 로 표현된다.
 */

export type SeedingCampaignKind = 'recruit' | 'propose';

export type SeedingRecruitStatus =
  | 'recruiting' // r-01 모집중
  | 'applied' // r-02 신청완료
  | 'selected' // r-03 선정완료(상품 준비중)
  | 'shipping' // r-04 배송중
  | 'delivered' // r-05 배송완료
  | 'contentRegistered' // r-06 콘텐츠 등록 완료
  | 'rejected' // r-07 신청 거절
  | 'closedSelecting'; // r-09 모집종료 + 선정중

export type SeedingProposeStatus =
  | 'pending' // p-01 수락전
  | 'productSelected' // p-02 옵션 선택 완료
  | 'preparing' // p-03 상품 준비중
  | 'shipping' // p-04 배송중
  | 'delivered' // p-05 배송완료
  | 'contentRegistered' // p-06 콘텐츠 등록 완료
  | 'soldOut' // p-07 품절(수락 전 단독 상품 품절)
  | 'rejected'; // p-08 제안 거절(종료) — 읽기전용 결과 화면 (SID-4210, 미선정 구조)

export type SeedingCampaignStatus = SeedingRecruitStatus | SeedingProposeStatus;

/** 콘텐츠 유형 — DTO 와 1:1 매핑 */
export type SeedingContentType = 'IMAGE' | 'SHORT_FORM' | 'LONG_FORM' | 'TEXT';

/** SNS 채널 — PRD §3-4. content-submission 라우트 query 와 1:1 매핑 (소문자 slug).
 *  MUSINSA_SNAP 은 무신사 전용·콘텐츠 제출 자체가 별도 webview 흐름 (PRD §3-4 update 4/29).
 *  런칭 미포함이라 클라이언트는 row 표기·라우팅 만 분기, BE 연동은 후속. */
export type SeedingSnsChannel =
  | 'INSTAGRAM'
  | 'YOUTUBE'
  | 'TWITTER'
  | 'TIKTOK'
  | 'NAVER_BLOG'
  | 'THREADS'
  | 'SNAP'
  | 'MUSINSA_SNAP';

export interface SeedingBrand {
  name: string;
  logoUrl: string;
  /**
   * 브랜드 식별자 (BE `campaign.brandId`) — 상품 카드 브랜드명 클릭 시 브랜드 스토어 홈 진입에 사용.
   * 무신사는 코드('032c'), 29CM 은 brandNo. 시딩은 단일 브랜드 캠페인이라 캠페인 브랜드 = 상품 브랜드.
   * (legacy mock 호환 위해 optional — 없으면 브랜드명 비클릭.) (QAT-39448)
   */
  brandId?: string;
}

export interface SeedingCountdown {
  /** 카운트다운 라벨 (예: "모집 마감까지", "수락까지 남은 시간") */
  label: string;
  /**
   * 종료 시각 (ms epoch). 24h 이상 → D-n, 미만 → hh:mm:ss.
   * null 이면 라벨/배너 만 노출 (예: p-03 "상품 준비중" 정보성).
   */
  targetAt: number | null;
  /**
   * 마감 시각 경과 시 label·카운트다운 대신 노출할 텍스트.
   * 예: delivered 상태에서 콘텐츠 제출 기한 경과 → "콘텐츠 제출 기한 경과". (SID-4178 작업2)
   */
  expiredLabel?: string | null;
}

export interface SeedingInfoRow {
  label: string;
  value: string;
}

export interface SeedingCampaignInfo {
  title: string;
  description: string;
  infoRows: SeedingInfoRow[];
}

export interface SeedingApplicationInfo {
  title: string;
  infoRows: SeedingInfoRow[];
}

/** 슬롯(아더컬러 그룹) 구성 goods 1개의 표시 메타 — 2depth 옵션 시트의 상품 선택 단계용. */
export interface SeedingProductSlotMember {
  goodsNo: number;
  brand: string;
  name: string;
  imageUrl: string;
  /** 상품 PDP 랜딩 URL — 썸네일 클릭 시 이동. (QAT-39446) */
  webUrl: string;
  price: number;
  soldOut: boolean;
}

export interface SeedingProduct {
  /** 슬롯 대표 goodsNo (단일 슬롯이면 그 goodsNo). */
  productId: number;
  /** 슬롯 번호 — 같은 slotNo = 아더컬러 그룹 (PRD 5-2). */
  slotNo: number;
  /**
   * 슬롯에 묶인 goods 들. length === 1 → 1depth("옵션 선택"),
   * length >= 2 → 아더컬러 2depth("상품/옵션 선택": 상품(컬러) 선택 → 옵션 선택).
   */
  members: SeedingProductSlotMember[];
  /** members.length >= 2 (아더컬러). */
  isMultiGoods: boolean;
  brand: string;
  name: string;
  category: string;
  imageUrl: string;
  /** 슬롯 대표 goods 의 PDP 랜딩 URL — 썸네일 클릭 시 이동. (QAT-39446) */
  webUrl: string;
  price: number;
  /** 옵션 선택 전: null, 선택 후: "블랙 / M" 등 */
  selectedOption: string | null;
  availableOptions?: string[];
  soldOut: boolean;
  /** CTA 라벨 (예: "옵션 선택" / "상품/옵션 선택"). null 이면 버튼 미노출 (read-only) */
  actionLabel: string | null;
  /**
   * 옵션/SKU 빈 항목 = 단품(29CM 옵션 없는 상품). 옵션 선택 UI 없이 신청/수락한다.
   * 두 신청/수락 페이지 overlay 가 29CM 한정으로 set. (SID-4296/4314)
   */
  isSingleProduct?: boolean;
}

export interface SeedingContentTypeOption {
  value: SeedingContentType;
  label: string;
  /** 선택 시 우측에 노출되는 업로드 기한 안내 (예: "배송 완료일로부터 7일 이내 업로드") — F12-2 */
  deadline?: string;
  /** 업로드 기한(영업일) 원시값 — 신청 확인 모달 등에서 "N일 이내" 동적 문구 조립용. (SID-4369) */
  uploadDeadlineDays?: number;
}

export interface SeedingContentTypeEditable {
  mode: 'editable';
  options: SeedingContentTypeOption[];
  selectedType: SeedingContentType | null;
  message: string;
  messagePlaceholder: string;
  messageMaxLength: number;
}

export interface SeedingContentTypeFrozen {
  mode: 'frozen';
  selectedType: SeedingContentType;
  selectedTypeLabel: string;
  /** 큐레이터가 작성한 자유 텍스트 (r-02 이후 read-only 표시) */
  message: string;
  /** 선택한 SNS 채널 표시 텍스트 (예: "유튜브") — F9-3 frozen-detail 노출용 */
  selectedSnsChannelLabel?: string;
  /** 업로드 기한 안내 (예: "배송 완료일로부터 10일 이내 업로드") — F9-3 frozen-detail 노출용 */
  uploadDeadlineNotice?: string;
}

export type SeedingContentTypeData = SeedingContentTypeEditable | SeedingContentTypeFrozen;

export interface SeedingContentGuide {
  hashtagDescription: string;
  hashtagText: string;
  hashtagNotice: string;
  brandRequest: string;
}

export type SeedingShippingTrackingStatus = 'PREPARING' | 'SHIPPING' | 'DELIVERED';

export type SeedingLabelVariant = 'redOpacity' | 'blueOpacity' | 'darkGray' | null;

export interface SeedingShippingBanner {
  title: string;
  subtitle?: string;
}

export interface SeedingShippingDetailRow {
  label: string;
  value: string;
  copyable?: boolean;
}

export interface SeedingShippingTracking {
  status: SeedingShippingTrackingStatus;
  /** "배송중" / "배송완료" / "콘텐츠 등록 완료" / null(준비중) */
  statusLabel: string | null;
  labelVariant: SeedingLabelVariant;
  banners: SeedingShippingBanner[];
  /** 택배사/운송장/일자 — 준비중 단계는 null */
  details: SeedingShippingDetailRow[] | null;
}

export interface SeedingContentSubmissionItem {
  /** 채널 식별자 — content-submission 라우트 query 매핑용. label 파싱 헬퍼 대체. */
  channel: SeedingSnsChannel;
  /** 큐레이터가 캠페인 신청 시 선택한 콘텐츠 유형 */
  contentType: SeedingContentType;
  /** 제출된 URL — null/undefined 이면 미제출 (edit mode 진입). 있으면 view mode. */
  submittedUrl?: string | null;
  label: string;
  subLabel?: string;
  /** "제출하기" / "수정하기" / null = row 자체 disabled (PRD §3-4 미제출+페널티 등) */
  actionLabel: string | null;
}

export interface SeedingContentSubmission {
  isComplete: boolean;
  items: SeedingContentSubmissionItem[];
}

export interface SeedingShippingAddress {
  /** 배송지 id — 신청/수락 payload 의 deliveryAddressId 폴백(무신사 등). 없으면 null. */
  deliveryAddressId?: number | null;
  recipient: string;
  phone: string;
  zipCode: string;
  address: string;
  addressDetail: string;
  /** 배송지 별칭('우리집' 등) — 신청/수락 payload addressName 폴백. 배송정보 완전 전송용. */
  addressName?: string | null;
  /** 배송 메모(요청사항) — 신청/수락 payload memo 폴백. */
  memo?: string | null;
  editable: boolean;
  /**
   * 등록된 배송지 존재 여부. false 면 "미설정" 안내 노출 (deliveryAddress == null).
   * adapter 가 명시적으로 set. 미지정(legacy mock) 시 등록된 것으로 간주.
   */
  registered?: boolean;
}

export interface SeedingNotice {
  title: string;
  subtitle: string;
  items: string[];
}

export interface SeedingRejected {
  reason: string;
  rejectedAt?: string;
}

/**
 * 큐레이터 활성 패널티 (PRD 5-4). 응답에 포함되면 항상 활성으로 간주.
 * 상세 최상단 고정 배너 + 신청·수락 CTA 비활성.
 */
export interface SeedingPenalty {
  reason: string;
}

export type SeedingCtaAction =
  | 'APPLY'
  | 'VIEW_APPLICATION'
  | 'CANCEL_APPLICATION'
  | 'ACCEPT'
  | 'REJECT'
  | 'VIEW_OTHER_CAMPAIGNS'
  // 제안형 품절 상세의 '확인' 버튼 — 직전 페이지로 복귀(router.back). (SID-4166)
  | 'GO_BACK';

export interface SeedingCtaButton {
  label: string;
  action: SeedingCtaAction;
  disabled?: boolean;
}

export interface SeedingPrimaryCtaSingle {
  mode: 'single';
  variant?: 'default' | 'blur';
  primary: SeedingCtaButton;
}

export interface SeedingPrimaryCtaDual {
  mode: 'dual';
  variant?: 'default' | 'blur';
  primary: SeedingCtaButton;
  secondary: SeedingCtaButton;
}

export type SeedingPrimaryCta = SeedingPrimaryCtaSingle | SeedingPrimaryCtaDual;

/** 시딩 캠페인 상세 — 17 상태를 단일 shape 으로 표현 */
export interface SeedingCampaignDetail {
  campaignId: number;
  /** 참여 ID — mutation(수락/거절/콘텐츠 제출) 호출에 필요. BE `participation.id`. 신청 전이면 null. */
  participationId?: number | null;
  kind: SeedingCampaignKind;
  status: SeedingCampaignStatus;
  /** 모집형 모집 마감 시각(KST ms). 만료 캠페인 직접 진입 시 신청 제출 차단 가드용. 제안형은 미지정. */
  recruitmentEndAtMs?: number | null;
  campaignName: string;
  brand: SeedingBrand;
  countdown: SeedingCountdown | null;
  campaignInfo: SeedingCampaignInfo;
  applicationInfo: SeedingApplicationInfo | null;
  products: SeedingProduct[];
  productSectionGuide: string | null;
  /** F18-1 — productSectionGuide 색상 emphasis. 'red' = 전체 품절 (진행 불가). 기본 = black */
  productSectionGuideEmphasis?: 'red' | 'default';
  contentType: SeedingContentTypeData;
  contentGuide: SeedingContentGuide;
  shippingTracking: SeedingShippingTracking | null;
  contentSubmission: SeedingContentSubmission | null;
  /**
   * 콘텐츠 제작 가이드 최하단 번호 — 값은 participation.id. 모집형 '신청 번호'(선정 후) /
   * 제안형 '제안 번호'(수락 후)로 타이틀만 상이. (SID-4331) 기존 selectionNumber 키 유지.
   */
  selectionNumber: string | null;
  shippingAddress: SeedingShippingAddress;
  /** @deprecated 모든 케이스 통일 카피로 `CampaignNotice` 정적 컴포넌트가 책임 (Figma 동일 instance 검증).
   *  필드는 mock 호환을 위해 optional 로 남김. BE 연동 시 필요 여부 재정의. */
  notice?: SeedingNotice;
  rejected: SeedingRejected | null;
  /**
   * 활성 패널티 (PRD 5-4). null/미지정이면 미노출. 존재 시 상단 배너 + 신청·수락 CTA 비활성.
   * adapter 가 명시적으로 set (legacy mock 호환 위해 optional).
   */
  penalty?: SeedingPenalty | null;
  primaryCta: SeedingPrimaryCta | null;
  /**
   * 캠페인 요구 SNS 채널 (BE `campaign.snsChannels`). 신청/수락 SNS 유효성 검사용.
   * 큐레이터 등록 채널과 교집합이 없으면 신청/수락 불가 (PRD §5-1 SNS 유효성 / TC-066·081).
   * adapter 가 명시적으로 set (legacy mock 호환 위해 optional).
   */
  requiredSnsChannels?: SeedingSnsChannel[];
}

/* ──────────────────────────────────────────────────────────── */
/*  Interaction Sheets (옵션 선택 / 거절 사유)                    */
/* ──────────────────────────────────────────────────────────── */

export interface ProductOptionItem {
  code: string;
  label: string;
  soldOut?: boolean;
  /** BE 컬러 코드(hex 아님). swatch 색 표현엔 미사용 — imageUrl 없을 때 흰색 폴백 여부만 판정. */
  hex?: string;
  /** 색상 칩 썸네일 URL. swatch 색 표현의 유일한 기준. */
  imageUrl?: string;
}

export interface OptionProductMulti {
  productId: string;
  brand: string;
  name: string;
  price: number;
  imageUrl: string;
  options: { sizes: ProductOptionItem[] };
}

export interface OptionMultiData {
  kind: 'multi';
  products: OptionProductMulti[];
}

export interface OptionProductSingle {
  productId: string;
  brand: string;
  name: string;
  price: number;
  imageUrl: string;
  options: { colors: ProductOptionItem[]; sizes: ProductOptionItem[] };
}

export interface OptionSingleData {
  kind: 'single';
  product: OptionProductSingle;
}

export type SeedingProductOptionPayload =
  | { mode: 'multi'; productId: string; size: string }
  | { mode: 'single'; productId: string; color: string; size: string };

export const PROPOSE_REJECT_REASON = {
  BRAND_IMAGE_MISMATCH: 'BRAND_IMAGE_MISMATCH',
  PRODUCT_PREFERENCE: 'PRODUCT_PREFERENCE',
  CONTENT_REQUIREMENTS: 'CONTENT_REQUIREMENTS',
  SCHEDULE_CONFLICT: 'SCHEDULE_CONFLICT',
  INSUFFICIENT_INFO: 'INSUFFICIENT_INFO',
  OTHER: 'OTHER',
} as const;

export type ProposeRejectReason =
  (typeof PROPOSE_REJECT_REASON)[keyof typeof PROPOSE_REJECT_REASON];

export interface ProposeRejectReasonItem {
  code: ProposeRejectReason;
  label: string;
}

export interface ProposeRejectPayload {
  reasons: ProposeRejectReason[];
}
